import React, { useEffect } from "react";
import { X, Lock, ShieldCheck, BadgeCheck, Loader2, ExternalLink } from "lucide-react";
import { SX } from "../studioTokens";
import { fmtNum, fmtDate, INTERVAL_LABEL } from "./billingConst";
import { CancelPlanPanel } from "../../../Account/billing/CancelPlanDialog";

const KEPT_ITEMS = ["Templates", "Documents", "Extraction results"];

const TRUST = [
  { icon: Lock, label: "256-bit SSL" },
  { icon: ShieldCheck, label: "PCI DSS" },
  { icon: BadgeCheck, label: "Stripe secure checkout" },
];

const Row = ({ label, value, muted, total }) => (
  <div className={`flex items-center justify-between gap-4 py-1.5 ${total ? "pt-3 mt-1.5 border-t border-dashed border-[#E9D2E6]" : ""}`}>
    <span className={total ? "text-[15px] font-semibold text-[#1A1A1A]" : `text-[13px] ${muted ? "text-[#8A8A90]" : "text-[#52525B]"}`}>
      {label}
    </span>
    <span className={total ? "text-[17px] font-semibold text-[#1A1A1A]" : `text-[13px] font-medium ${muted ? "text-[#8A8A90]" : "text-[#1A1A1A]"}`}>
      {value}
    </span>
  </div>
);

/* Confirmation step in front of every billing mutation. Checkout and add-on
   purchases end in a redirect to Stripe's hosted page rather than an in-app
   card form, so this modal is a summary + handoff, not a payment form. */
const BillingConfirm = ({ intent, busy, onClose, onConfirm }) => {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape" && !busy) onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [busy, onClose]);

  if (!intent) return null;
  const { mode, plan, addon, currentPlanName, transition, periodEnd } = intent;

  // Cancelling looks the same as on the RFP Manager tab.
  if (mode === "cancel") {
    return (
      <CancelPlanPanel
        open
        onClose={busy ? () => {} : onClose}
        title={`Cancel your ${currentPlanName} plan?`}
        keptItems={KEPT_ITEMS}
        saving={busy}
        onConfirm={onConfirm}
      >
        You keep {currentPlanName} and your remaining credits until{" "}
        <b className="font-semibold text-[#1A1A1A]">{fmtDate(periodEnd)}</b>. After that, extraction stops until you
        subscribe again.
      </CancelPlanPanel>
    );
  }

  const copy = {
    checkout: {
      title: `Subscribe to ${plan?.name}`,
      sub: "You'll finish payment on Stripe's secure checkout page.",
      cta: "Continue to Stripe",
      icon: ExternalLink,
    },
    addon: {
      title: "Buy add-on credits",
      sub: "A one-off charge — credits land in your balance once Stripe confirms.",
      cta: "Continue to Stripe",
      icon: ExternalLink,
    },
    change: {
      title: `Switch to ${plan?.name}`,
      sub: transition === "immediate"
        ? "This takes effect right away."
        : "This is scheduled for the end of your current billing period.",
      cta: "Confirm change",
      icon: null,
    },
  }[mode];

  const CtaIcon = copy.icon;

  return (
    <div
      className="fixed inset-0 bg-[rgba(20,10,24,0.34)] backdrop-blur-[2px] z-[140] flex items-center justify-center p-6"
      onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={copy.title}
        className="w-full max-w-[480px] max-h-[90vh] overflow-auto bg-white rounded-[16px] shadow-[-2px_4px_20px_6px_rgba(0,0,0,0.14)] font-outfit"
      >
        <div className="flex items-start justify-between gap-3 px-6 pt-5">
          <div>
            <div className="text-[16.5px] font-semibold tracking-[-0.02em] text-[#1A1A1A]">{copy.title}</div>
            <div className="text-[12.5px] text-[#52525B] mt-1">{copy.sub}</div>
          </div>
          <button
            className="w-8 h-8 rounded-[9px] border border-[#ECE9EE] bg-white text-[#52525B] flex items-center justify-center flex-shrink-0 hover:enabled:bg-[#F5F5F5] disabled:opacity-40"
            onClick={onClose}
            disabled={busy}
            aria-label="Close"
          >
            <X size={15} />
          </button>
        </div>

        <div className="px-6 pt-4 pb-6">
          <div className="rounded-[12px] border border-[#E9D2E6] bg-[#FBF5FA] px-4 py-3.5">
            {mode === "addon" ? (
              <>
                <Row label={addon.name} value={`$${fmtNum(addon.price_usd)}`} />
                <Row label="Credits added" value={`+${fmtNum(addon.credits)}`} muted />
                <Row label="Tax" value="Calculated at payment" muted />
                <Row label="Total today" value={`$${fmtNum(addon.price_usd)}`} total />
              </>
            ) : mode === "checkout" ? (
              <>
                <Row label={`${plan.name} plan`} value={`$${fmtNum(plan.price_usd)} ${INTERVAL_LABEL[plan.billing_interval] || ""}`} />
                <Row label="Credits included" value={`${fmtNum(plan.credits)} ${plan.billing_interval === "annual" ? "per year" : "per month"}`} muted />
                <Row label="Tax" value="Calculated at payment" muted />
                <Row label="Billed today" value={`$${fmtNum(plan.price_usd)}`} total />
              </>
            ) : (
              <>
                <Row label="Current plan" value={currentPlanName} muted />
                <Row label="New plan" value={plan.name} />
                <Row label="New allocation" value={`${fmtNum(plan.credits)} credits`} muted />
                <Row
                  label={transition === "immediate" ? "Takes effect" : "Effective from"}
                  value={transition === "immediate" ? "Immediately" : fmtDate(periodEnd)}
                  total
                />
              </>
            )}
          </div>

          {mode === "change" && (
            <p className="mt-3.5 text-[12px] leading-[1.5] text-[#8A8A90]">
              {transition === "immediate"
                ? "Stripe raises a prorated invoice for the difference. Your new allocation appears here once the payment confirms — usually within a few seconds."
                : "You keep your current allocation and price until the period ends. Nothing is charged today."}
            </p>
          )}

          <div className="flex items-center gap-2.5 mt-5">
            <button className={`${SX.btn} ${SX.btnGhost} flex-1 justify-center`} onClick={onClose} disabled={busy}>
              Back
            </button>
            <button
              className={`${SX.btn} ${SX.btnPrimary} flex-1 justify-center`}
              onClick={onConfirm}
              disabled={busy}
            >
              {busy ? <Loader2 size={14} className="animate-spin" /> : CtaIcon ? <CtaIcon size={14} /> : null}
              {copy.cta}
            </button>
          </div>

          {(mode === "checkout" || mode === "addon") && (
            <div className="flex items-center justify-center gap-4 flex-wrap mt-4">
              {TRUST.map(({ icon: Icon, label }) => (
                <span key={label} className="inline-flex items-center gap-1.5 text-[11.5px] font-medium text-[#8A8A90]">
                  <Icon size={13} className="text-[#2F7D44]" /> {label}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BillingConfirm;
